import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { VisitOutcome, outcomeSide } from "../../features/sessions/models/VisitOutcome";
import AttendanceOutcomeControl from "./AttendanceOutcomeControl";
import { ROUTES } from "../../routes";

interface AttendanceRowProps {
  name: string;
  /** When set, the name links to the member's detail page. */
  memberId?: string;
  /** The member declared an absence covering this occurrence's date. Display-only. */
  away?: boolean;
  outcome: VisitOutcome;
  onChange: (outcome: VisitOutcome) => void;
  /** The Visit's own Payment Type name (e.g. a late-cancellation fee). */
  feeName?: string;
}

const INITIALS_PALETTE = ["#367D7D", "#BF4095", "#CC4D33", "#945EBA"];

function getInitials(name: string): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

function pickColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return INITIALS_PALETTE[hash % INITIALS_PALETTE.length];
}

const AttendanceRow: React.FC<AttendanceRowProps> = ({
  name,
  memberId,
  away,
  outcome,
  onChange,
  feeName,
}) => {
  const { t } = useTranslation();
  const side = outcomeSide(outcome);
  const dimmed = side === "absent";
  const nameClasses = `truncate text-sm font-medium ${
    dimmed
      ? "text-grey-40 line-through dark:text-grey-60"
      : "text-grey-10 dark:text-grey-90"
  }`;

  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="flex min-w-0 items-center gap-3">
        <div
          className="flex size-9 shrink-0 items-center justify-center rounded-full border border-white"
          style={{ backgroundColor: pickColor(name) }}
        >
          <span className="text-xs font-bold text-white">{getInitials(name)}</span>
        </div>
        {memberId ? (
          <Link
            to={ROUTES.MEMBER_DETAILS.replace(":id", memberId)}
            className={`${nameClasses} cursor-pointer hover:underline`}
          >
            {name}
          </Link>
        ) : (
          <span className={nameClasses}>{name}</span>
        )}
        {away && (
          <span className="shrink-0 rounded-full bg-primary-95 px-2 py-0.5 text-xs font-medium text-primary-40 dark:bg-primary-20 dark:text-primary-90">
            {t("absences.awayBadge", "Away")}
          </span>
        )}
      </div>
      <AttendanceOutcomeControl outcome={outcome} onChange={onChange} feeName={feeName} />
    </div>
  );
};

export default AttendanceRow;
